// src/components/sla/ai/SlaEscalationMailPanel.js
// ─────────────────────────────────────────────────────────────────────────────
// Mail Center tab: the escalation emails the SLA AI sent to supervisors / managers —
// who received them, which failed — and "Draft follow-up", which pre-fills the
// Compose tab (subject, body with the AI's explanation, link to the work order).
// ─────────────────────────────────────────────────────────────────────────────
import React, { memo, useCallback, useState } from 'react';
import {
    Alert, AlertDescription, AlertIcon, Badge, Box, Button, Flex, HStack, Icon, IconButton, Select, Skeleton, Stack, Table,
    Tbody, Td, Text, Th, Thead, Tooltip, Tr, useColorModeValue, useDisclosure,
} from '@chakra-ui/react';
import { MdAutoAwesome, MdEdit, MdEmail, MdErrorOutline, MdOpenInNew, MdRefresh } from 'react-icons/md';
import { useSlaAiDecisions } from '../../../hooks/useSlaAi';
import SlaDecisionExplainer from './SlaDecisionExplainer';
import { ACTION_META, fmtDateTime } from './slaAiFormat';

const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/** Follow-up email draft that carries the AI's explanation. */
export function buildFollowUpDraft(d) {
    const meta = ACTION_META[d.decision_type] || ACTION_META.escalate;
    const reasons = (d.reasons || []).map(r => `<li>${esc(r)}</li>`).join('');
    return {
        subject: `Follow-up: ${meta.short} — WO #${d.work_order_id}${d.work_order_title ? ` ${d.work_order_title}` : ''}`,
        body: `<p>Hello,</p><p>Following up on the FlowSense AI SLA action for <b>Work Order #${esc(d.work_order_id)}</b>${d.work_order_title ? ` (${esc(d.work_order_title)})` : ''}.</p>`
            + `<p><b>What FlowSense AI did:</b> ${esc(d.summary)}</p>${reasons ? `<p><b>Why:</b></p><ul>${reasons}</ul>` : ''}`
            + '<p>Please confirm the current status and expected completion time.</p><p>Thank you.</p>',
        linkType: 'work_order',
        linkId: String(d.work_order_id),
    };
}

function SlaEscalationMailPanel({ onDraft }) {
    const [type, setType] = useState('all');
    const feed = useSlaAiDecisions({ type: type === 'all' ? undefined : type }, { pageSize: 30 });
    const [selected, setSelected] = useState(null);
    const drawer = useDisclosure();
    const muted = useColorModeValue('gray.500', 'gray.400');
    const { refresh } = feed;
    const rows = feed.rows.filter(d => d.decision_type !== 'warning' || (d.notified?.email?.sent || []).length);
    const open = useCallback((d) => { setSelected(d); drawer.onOpen(); }, [drawer]);

    return (
        <Box>
            <Flex justify="space-between" align="center" mb={4} gap={2} flexWrap="wrap">
                <HStack><Icon as={MdAutoAwesome} color="purple.500" boxSize={6} /><Text fontSize="lg" fontWeight="800">AI escalation emails</Text></HStack>
                <HStack>
                    <Select size="sm" w="auto" value={type} onChange={(e) => setType(e.target.value)} aria-label="Action type" borderRadius="md">
                        <option value="all">All actions</option>
                        <option value="reassign">Reassigned</option>
                        <option value="escalate">Escalated</option>
                        <option value="recommend">Recommended</option>
                    </Select>
                    <IconButton size="sm" variant="ghost" icon={<MdRefresh />} aria-label="Refresh" onClick={refresh} isLoading={feed.refreshing} />
                </HStack>
            </Flex>
            <Text fontSize="sm" color={muted} mb={3}>
                When a job breaches its SLA, FlowSense AI emails every supervisor / manager with what it did and why. Use “Draft follow-up” to reply from the Mail Center.
            </Text>
            {feed.error && <Alert status="error" borderRadius="lg" mb={3}><AlertIcon /><AlertDescription flex="1">{feed.error}</AlertDescription><Button size="xs" onClick={() => feed.reload()}>Retry</Button></Alert>}
            {feed.loading ? <Stack>{[0, 1, 2].map(i => <Skeleton key={i} h="48px" borderRadius="md" />)}</Stack> : rows.length === 0 ? (
                <Text color={muted} py={6} textAlign="center">No AI escalation emails yet.</Text>
            ) : (
                <Box overflowX="auto" borderWidth="1px" borderRadius="xl">
                    <Table size="sm">
                        <Thead><Tr><Th>When</Th><Th>Action</Th><Th>Work order</Th><Th>Recipients</Th><Th isNumeric>Actions</Th></Tr></Thead>
                        <Tbody>
                            {rows.map(d => {
                                const meta = ACTION_META[d.decision_type] || ACTION_META.escalate;
                                const sent = d.notified?.email?.sent || [];
                                const failed = d.notified?.email?.failed || [];
                                return (
                                    <Tr key={d.id}>
                                        <Td whiteSpace="nowrap" fontSize="sm">{fmtDateTime(d.created_at)}</Td>
                                        <Td><Badge colorScheme={meta.scheme}>{meta.short}</Badge>{d.escalation_level ? <Badge ml={1} variant="outline" colorScheme="red">L{d.escalation_level}</Badge> : null}</Td>
                                        <Td maxW="320px"><Text fontSize="sm" fontWeight="700" noOfLines={1}>#{d.work_order_id} {d.work_order_title}</Text><Text fontSize="xs" color={muted} noOfLines={1}>{d.summary}</Text></Td>
                                        <Td>
                                            <HStack spacing={2}>
                                                <Tooltip label={sent.join(', ') || 'No email sent'} hasArrow><HStack spacing={1}><Icon as={MdEmail} color="blue.400" /><Text fontSize="sm">{sent.length}</Text></HStack></Tooltip>
                                                {failed.length > 0 && <Tooltip label={failed.map(f => `${f.to}: ${f.error}`).join('\n')} hasArrow><HStack spacing={1}><Icon as={MdErrorOutline} color="red.400" /><Text fontSize="sm" color="red.400">{failed.length}</Text></HStack></Tooltip>}
                                            </HStack>
                                        </Td>
                                        <Td isNumeric>
                                            <HStack justify="flex-end" spacing={1}>
                                                <Tooltip label="Why the AI did this" hasArrow><IconButton size="xs" variant="ghost" icon={<MdOpenInNew />} aria-label="Open decision" onClick={() => open(d)} /></Tooltip>
                                                {onDraft && <Button size="xs" leftIcon={<MdEdit />} colorScheme="purple" variant="outline" onClick={() => onDraft(buildFollowUpDraft(d))}>Draft follow-up</Button>}
                                            </HStack>
                                        </Td>
                                    </Tr>
                                );
                            })}
                        </Tbody>
                    </Table>
                </Box>
            )}
            {feed.hasMore && <Button mt={3} size="sm" variant="ghost" w="100%" onClick={feed.loadMore}>Load more</Button>}
            <SlaDecisionExplainer isOpen={drawer.isOpen} onClose={drawer.onClose} decision={selected} decisionId={selected?.id} onChanged={refresh} />
        </Box>
    );
}

export default memo(SlaEscalationMailPanel);
